import { isPermitted, mapRoles } from 'src/js/userdata.js';
import { userFields } from 'src/js/constants';
import { datadogRum } from 'src/js/datadog.js';
import { readItems, readMe, updateMe } from '@directus/sdk';

export function getDirectusInstance() {
  if (typeof window !== 'undefined' && window.$crm) {
    return window.$crm;
  }

  const { createDirectus, rest, staticToken, authentication } = require('@directus/sdk');
  const authToken = localStorage.getItem('crm_auth_token');
  let client = createDirectus(process.env.CRM_URL)
    .with(rest())
    .with(authentication('json'));
  
  if (authToken) {
    client = client.with(staticToken(authToken));
  }

  return client;
}

let authCache = {
  data: null,
  token: null,
  timestamp: null,
  ttl: 12 * 60 * 1000
};

export function isToken() {
  const auth_token = localStorage.getItem('crm_auth_token');
  return auth_token != null && typeof auth_token != 'undefined';
}

function validUserObject(user) {
  try {
    const userKeys = Object.keys(user);

    for (let i = 0; i <= userKeys.length - 1; i++) {
      if (!userFields.includes(userKeys[i])) {
        return false;
      }
    }
  } catch (e) {
    console.log(e);
    return false;
  }
  return true;
}

export async function doesPermittedUserExist(user) {
  try {
    if (isToken()) {
      const directus = getDirectusInstance();
      try {
        const currentUser = await directus.request(readMe());
        if (validUserObject(currentUser)) {
          if (currentUser.status === 'active' && isPermitted(currentUser.role)) {
            return true;
          }
        }
      } catch (e) {
        return false;
      }
    }
  } catch (e) {
    console.log(e);
    return false;
  }
  return false;
}

export async function authByToken() {
  const auth_token = localStorage.getItem('crm_auth_token');
  if (auth_token === null || typeof auth_token === 'undefined') {
    clearAuthCache();
    return false;
  } else if (Object.prototype.toString.call(auth_token) === '[object String]') {
    if (
      authCache.data &&
      authCache.token === auth_token &&
      authCache.timestamp &&
      Date.now() - authCache.timestamp < authCache.ttl
    ) {
      return authCache.data;
    }    

    try {
        const directus = getDirectusInstance();
        const privateData = await directus.request(readMe({
          fields: userFields
        }));

        if (validUserObject(privateData)) {
          const auth_token = localStorage.getItem('crm_auth_token');
          const crmUrl = process.env.CRM_URL;
          const rolesResponse = await fetch(`${crmUrl}roles?limit=-1`, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${auth_token}`,
              'Content-Type': 'application/json'
            }
          });
          const rolesData = await rolesResponse.json();
          privateData.role = await mapRoles(rolesData.data || rolesData, privateData.role);
          
          if (privateData?.main_pharmacy) {
            const mainPharmacyUser = await getMainPharmacyUser(privateData.main_pharmacy, ['show_import_csv_tab', 'enable_doctors_csv_report', 'allowed_delivery_sources', 'delivery_request_timeout']);
            if (mainPharmacyUser) {
              privateData.show_import_csv_tab = mainPharmacyUser.show_import_csv_tab;
              privateData.enable_doctors_csv_report = mainPharmacyUser.enable_doctors_csv_report;
              privateData.allowed_delivery_sources = mainPharmacyUser?.allowed_delivery_sources;
              privateData.delivery_request_timeout = mainPharmacyUser?.delivery_request_timeout;
            }
          }
          datadogRum.setGlobalContextProperty('main_pharmacy_id', privateData?.main_pharmacy)

          if (privateData.status === 'active' && isPermitted(privateData.role)) {
            const result = { ...privateData };
            authCache.data = result;
            authCache.token = auth_token;
            authCache.timestamp = Date.now();
            return result;
        }
      }
    } catch (exception) {
      console.log('Something went wrong while logging in: ', exception);
      clearAuthCache();
      return false;
    }
  }

  clearAuthCache();
  return false;
}

export function clearAuthCache() {
  authCache.data = null;
  authCache.token = null;
  authCache.timestamp = null;
}

export async function refreshAuthCache() {
  clearAuthCache();
  return await authByToken();
}

export async function readCrmPharmacyUser() {
  const auth_token = localStorage.getItem('crm_auth_token');

  if (auth_token === null || typeof auth_token === 'undefined') {
    return false;
  } else {
   try {
     const directus = getDirectusInstance();
     const privateData = await directus.request(readMe());
     return privateData;
   } catch (e) {
       console.log('Something went wrong while logging in: ', e);
       return false;
   }
  }
}

export async function updatePackagingTypeCrmPharmacy(data) {
  const auth_token = localStorage.getItem('crm_auth_token');

  if (auth_token === null || typeof auth_token === 'undefined') {
    return false;
  } else {
    try {
      const directus = getDirectusInstance();
      const privateData = await directus.request(updateMe(data));
      return privateData;
    } catch (e) {
      console.log('Something went wrong while logging in: ', e);
      return false;
    }
  }
}

export async function updatePharmacyScalesCrmPharmacy(scales) {
  const auth_token = localStorage.getItem('crm_auth_token');

  if (auth_token === null || typeof auth_token === 'undefined') {
    return false;
    } else {
    try {
      const directus = getDirectusInstance();
      const privateData = await directus.request(updateMe(scales));
      return privateData;
    } catch (e) {
      console.log('Something went wrong while logging in: ', e);
      return false;
    }
  }
}

export async function updateManufacturingInstrCrmPharmacyUser(items) {
  const auth_token = localStorage.getItem('crm_auth_token');

  if (auth_token === null || typeof auth_token === 'undefined') {
    return false;
  } else {
    try {
      const directus = getDirectusInstance();
      const privateData = await directus.request(updateMe(items));
      return privateData;
    } catch (e) {
      console.log('Something went wrong while logging in: ', e);
      return false;
    }
  }
}

export async function getToken(key, token) {
  if (key) {
    const directus = getDirectusInstance();
    const query = {
      filter: {
        key: {
          _eq: key
        }
      },
      limit: 1
    };

    let result = await directus.request(readItems('tokens', query));

    return result[0]?.value;
  }
}

export async function getCredentials(key) {
  if (key) {
    const directus = getDirectusInstance();
    const query = {
      filter: {
        key: {
          _eq: key
        }
      },
      fields: ['fastbill_api_key', 'fastbill_email', "cardflo_merchant_id"],
      limit: 1
    };

    const result = await directus.request(readItems('tokens', query));

    if (!result && !Array.isArray(result) && result.length !== 1) {
      throw new Error('token not found');
    }

    return {
      fastbill_api_key: result[0].fastbill_api_key,
      fastbill_email: result[0].fastbill_email,
      cardflo_merchant_id: result[0].cardflo_merchant_id
    };
  }
}

export async function getPharmacyOwner(key) {
  if (key) {
    const directus = getDirectusInstance();
    const query = {
      filter: {
        key: {
          _eq: key
        }
      },
      fields: ['pharmacy_owner'],
      limit: 1
    };

    const result = await directus.request(readItems('tokens', query));

    if (!result || !Array.isArray(result) || result.length !== 1) {
      return null;
    }

    return result[0]?.pharmacy_owner;
  }
  return null;
}

export async function getMainPharmacyUser(mainPharmacyId, fields = [], authToken = null) {
  if (!mainPharmacyId) {
    return null;
  }

  const auth_token = authToken || localStorage.getItem('crm_auth_token');
  if (!auth_token) {
    return null;
  }

  const crmUrl = process.env.CRM_URL;
  const fieldsParam = Array.isArray(fields) ? fields.join(',') : fields;
  
  try {
    const response = await fetch(`${crmUrl}users/${mainPharmacyId}?fields=${fieldsParam}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${auth_token}`,
        'Content-Type': 'application/json'
      }
    });
    
    const data = await response.json();
    return data.data || data;
  } catch (error) {
    console.error('Error fetching main pharmacy user:', error);
    return null;
  }
}